import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';
import { apiCall } from '../services/realApi';
import { demoModeManager } from '../services/demoModeManager';
import { workloadsApi } from '../services/workloadsApi';
import type { ProjectSummary } from '../types/projectSummary';
import type { WorkloadSummary } from '../types/workload';

export type FilterType = 'all' | 'workload' | 'project' | 'account' | 'environment';

export interface AWSAccountSummary {
  id: string;
  account_id: string; // 12-digit AWS account ID
  account_type: string;
  client_id: string;
}

export interface AWSEnvironmentSummary {
  id: string;
  name: string;
  environment_type?: string;
  aws_account_id: string;
  aws_account?: { id: string; account_id: string; account_type: string };
}

interface FilterState {
  filterType: FilterType;
  selectedId: string | null;
}

export interface FilterContextValue {
  // Current filter
  filterType: FilterType;
  selectedId: string | null;

  // Data lists
  projects: ProjectSummary[];
  workloads: WorkloadSummary[];
  accounts: AWSAccountSummary[];
  environments: AWSEnvironmentSummary[];

  // Refresh
  refreshFilterData: () => void;

  // Loading
  isLoading: boolean;

  // Actions
  selectFilter: (type: FilterType, id: string | null) => void;

  // Backward compat with ProjectContext
  selectedProjectId: string | null;
  hasProjectAccess: boolean;
}

const FILTER_STORAGE_KEY = 'globalFilter';
const PROJECT_STORAGE_KEY = 'selectedProjectId';

const DEMO_SUPPORTED_PROJECT_IDS = new Set([
  'p0000000-0000-4000-a000-000000000001',
  'p0000000-0000-4000-a000-000000000002',
  'p0000000-0000-4000-a000-000000000003',
]);

const FilterContext = createContext<FilterContextValue | null>(null);

export const useFilter = (): FilterContextValue => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { selectedClientId } = useClient();

  // Initialize from localStorage
  const [filterState, setFilterState] = useState<FilterState>(() => {
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { filterType: parsed.filterType || 'all', selectedId: parsed.selectedId || null };
      }
      // Migrate from old ProjectContext storage
      const oldProjectId = localStorage.getItem(PROJECT_STORAGE_KEY);
      if (oldProjectId) {
        return { filterType: 'project' as FilterType, selectedId: oldProjectId };
      }
    } catch {
      // ignore
    }
    return { filterType: 'all', selectedId: null };
  });

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [workloads, setWorkloads] = useState<WorkloadSummary[]>([]);
  const [accounts, setAccounts] = useState<AWSAccountSummary[]>([]);
  const [environments, setEnvironments] = useState<AWSEnvironmentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const hasProjectAccess = useMemo(() => {
    if (!user) return false;
    if (user.is_anonymous) return false;
    if (user.client?.subscription_plan === 'assessment_only') return false;
    return true;
  }, [user]);

  const effectiveClientId = useMemo(() => {
    if (!user) return null;
    if (user.role === 'client') return user.client?.id ?? null;
    return selectedClientId;
  }, [user, selectedClientId]);

  // Load projects, workloads, and accounts
  const loadData = useCallback(async () => {
    if (!hasProjectAccess || !effectiveClientId) {
      setProjects([]);
      setWorkloads([]);
      setAccounts([]);
      return;
    }

    setIsLoading(true);
    try {
      const isDemoMode = demoModeManager.getDemoMode().isDemoMode;

      // Load projects
      const projectEndpoint = user?.role === 'client'
        ? '/projects'
        : `/projects?client_id=${effectiveClientId}`;
      const projectData = await apiCall(projectEndpoint);
      const rawProjectList: ProjectSummary[] = Array.isArray(projectData) ? projectData : [];
      const projectList = isDemoMode
        ? rawProjectList.filter((p) => DEMO_SUPPORTED_PROJECT_IDS.has(p.id))
        : rawProjectList;
      setProjects(projectList);

      // Load workloads
      let loadedWorkloads: WorkloadSummary[] = [];
      try {
        const wlData = await workloadsApi.getAll(effectiveClientId);
        loadedWorkloads = Array.isArray(wlData) ? wlData : [];
      } catch {
        loadedWorkloads = [];
      }
      setWorkloads(loadedWorkloads);

      // Load AWS accounts
      let loadedAccounts: AWSAccountSummary[] = [];
      try {
        const accData = await apiCall(`/aws/accounts?client_id=${effectiveClientId}`);
        loadedAccounts = Array.isArray(accData) ? accData : [];
      } catch {
        loadedAccounts = [];
      }
      setAccounts(loadedAccounts);

      // Load AWS environments
      let loadedEnvironments: AWSEnvironmentSummary[] = [];
      try {
        const envData = await apiCall(`/aws/environments?client_id=${effectiveClientId}`);
        loadedEnvironments = Array.isArray(envData) ? envData : [];
      } catch {
        loadedEnvironments = [];
      }
      setEnvironments(loadedEnvironments);

      // Validate stored filter selection
      const { filterType: ft, selectedId: sid } = filterState;
      if (sid) {
        let isValid = false;
        if (ft === 'project') {
          isValid = projectList.some(p => p.id === sid);
        } else if (ft === 'workload') {
          isValid = loadedWorkloads.some(w => w.id === sid);
        } else if (ft === 'account') {
          isValid = loadedAccounts.some(a => a.id === sid);
        } else if (ft === 'environment') {
          isValid = loadedEnvironments.some(e => e.id === sid);
        }
        if (!isValid) {
          setFilterState({ filterType: 'all', selectedId: null });
          localStorage.removeItem(FILTER_STORAGE_KEY);
          localStorage.removeItem(PROJECT_STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error('Error loading filter data:', err);
      setProjects([]);
      setWorkloads([]);
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProjectAccess, effectiveClientId, user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset filter when client changes (non-client users)
  useEffect(() => {
    if (user?.role !== 'client') {
      setFilterState({ filterType: 'all', selectedId: null });
      localStorage.removeItem(FILTER_STORAGE_KEY);
      localStorage.removeItem(PROJECT_STORAGE_KEY);
    }
  }, [selectedClientId, user?.role]);

  const selectFilter = useCallback((type: FilterType, id: string | null) => {
    const newState: FilterState = { filterType: type, selectedId: id };
    setFilterState(newState);

    if (type === 'all' || !id) {
      localStorage.removeItem(FILTER_STORAGE_KEY);
      localStorage.removeItem(PROJECT_STORAGE_KEY);
    } else {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(newState));
      // Backward compat: keep selectedProjectId in sync for API services
      if (type === 'project') {
        localStorage.setItem(PROJECT_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(PROJECT_STORAGE_KEY);
      }
    }
  }, []);

  // Derived backward-compat values
  const selectedProjectId = filterState.filterType === 'project' ? filterState.selectedId : null;

  const value: FilterContextValue = {
    filterType: filterState.filterType,
    selectedId: filterState.selectedId,
    projects,
    workloads,
    accounts,
    environments,
    isLoading,
    selectFilter,
    refreshFilterData: () => { void loadData(); },
    selectedProjectId,
    hasProjectAccess,
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};
